import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  CircularProgress,
  Alert,
  Divider,
  Snackbar,
  Grid 
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  ArrowBack
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import formularioService from '../../services/formularioService';

/**
 * Componente para visualizar un PDF rellenado con datos de un formulario
 * Este componente permite:
 * 1. Ver el PDF generado con los datos del formulario
 * 2. Descargar el PDF
 * 3. Imprimir el PDF
 */
const FormularioPdfViewer = () => {
  // Obtener parámetros de la URL
  const { formularioId, respuestaId } = useParams();
  const navigate = useNavigate();
  
  // Estados
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [formulario, setFormulario] = useState(null);
  const [respuesta, setRespuesta] = useState(null);
  const [generandoPdf, setGenerandoPdf] = useState(false);
  
  // Cargar datos al montar el componente
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Cargar formulario
        const formularioData = await formularioService.getFormulario(formularioId);
        setFormulario(formularioData);
        
        // Cargar respuesta
        const respuestaData = await formularioService.getRespuesta(respuestaId);
        setRespuesta(respuestaData);
        
        // Si la respuesta ya tiene un PDF generado, mostrar
        if (respuestaData.documento_generado_id) {
          // Buscar URL del documento
          if (respuestaData.documento && respuestaData.documento.ruta) {
            setPdfUrl(respuestaData.documento.ruta);
          } else {
            // Si no tiene documento asociado, generarlo
            await generarPDF();
          }
        } else {
          // Si no tiene documento, generar PDF
          await generarPDF();
        }
      } catch (err) {
        console.error('Error al cargar datos:', err);
        setError('No se pudo generar el PDF. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };
    
    const generarPDF = async () => {
      try {
        setGenerandoPdf(true);
        
        // Generar PDF
        const resultado = await formularioService.generarPDF(formularioId, respuestaId);
        
        // Establecer URL del PDF
        if (resultado.url) {
          setPdfUrl(resultado.url);
        } else {
          throw new Error('No se recibió URL del PDF generado');
        }
      } catch (err) {
        console.error('Error al generar PDF:', err);
        setError('Error al generar el PDF: ' + (err.message || err));
      } finally {
        setGenerandoPdf(false);
      }
    };
    
    if (formularioId && respuestaId) {
      cargarDatos();
    } else {
      setError('Parámetros faltantes: se requiere formularioId y respuestaId');
      setLoading(false);
    }
  }, [formularioId, respuestaId]);
  
  // Manejar impresión del PDF
  const handlePrint = () => {
    if (!pdfUrl) return;
    
    const printWindow = window.open(pdfUrl, '_blank');
    if (printWindow) {
      printWindow.addEventListener('load', () => {
        printWindow.print();
      });
    } else {
      alert('Por favor permita ventanas emergentes para imprimir');
    }
  };
  
  // Manejar descarga del PDF
  const handleDownload = () => {
    if (!pdfUrl) return;
    
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `${formulario?.nombre || 'formulario'}_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Regenerar PDF
  const handleRegenerarPDF = async () => {
    try {
      setGenerandoPdf(true);
      setError(null);
      
      // Generar PDF nuevamente
      const resultado = await formularioService.generarPDF(formularioId, respuestaId);
      
      // Establecer URL del PDF
      if (resultado.url) {
        setPdfUrl(resultado.url);
      } else {
        throw new Error('No se recibió URL del PDF generado');
      }
    } catch (err) {
      console.error('Error al regenerar PDF:', err);
      setError('Error al regenerar el PDF: ' + (err.message || err));
    } finally {
      setGenerandoPdf(false);
    }
  };
  
  // Si está cargando, mostrar indicador
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Alertas de error */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      {/* Cabecera */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate(-1)}
              sx={{ mr: 2 }}
            >
              Volver
            </Button>
            <Typography variant="h4" component="h1">
              {formulario?.nombre || 'Visualizador de PDF'}
            </Typography>
          </Box>
          
          <Box>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={!pdfUrl || generandoPdf}
              sx={{ mr: 1 }}
            >
              Imprimir
            </Button>
            
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={!pdfUrl || generandoPdf}
            >
              Descargar PDF
            </Button>
          </Box>
        </Box>
        
        {formulario?.descripcion && (
          <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
            {formulario.descripcion}
          </Typography>
        )}
        
        {respuesta?.paciente && (
          <Box mt={2}>
            <Typography variant="subtitle1">
              Paciente: {respuesta.paciente.nombres} {respuesta.paciente.apellidos}
            </Typography>
            {respuesta.fecha_completado && (
              <Typography variant="subtitle2" color="textSecondary">
                Completado: {new Date(respuesta.fecha_completado).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        )}
      </Paper>
      
      {/* Error persistente */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Visor de PDF */}
      <Paper 
        variant="outlined"
        sx={{
          width: '100%',
          height: 700,
          overflow: 'hidden',
          position: 'relative',
          mb: 3
        }}
      >
        {generandoPdf ? (
          <Box 
            display="flex" 
            flexDirection="column"
            justifyContent="center" 
            alignItems="center"
            height="100%"
          >
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1">
              Generando PDF...
            </Typography>
          </Box>
        ) : pdfUrl ? (
          <iframe
            src={pdfUrl}
            width="100%"
            height="100%"
            title="Vista previa del PDF"
            style={{ border: 'none' }}
          />
        ) : (
          <Box 
            display="flex" 
            flexDirection="column"
            justifyContent="center" 
            alignItems="center"
            height="100%"
          >
            <PdfIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No hay PDF disponible
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRegenerarPDF}
              sx={{ mt: 2 }}
            >
              Generar PDF
            </Button>
          </Box>
        )}
      </Paper>
      
      {/* Información adicional */}
      {respuesta && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Información del Documento
          </Typography>
          
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2">
                <strong>Formulario:</strong> {formulario?.nombre}
              </Typography>
              
              <Typography variant="body2">
                <strong>Fecha de completado:</strong> {new Date(respuesta.fecha_completado).toLocaleDateString()}
              </Typography>
              
              <Typography variant="body2">
                <strong>Paciente:</strong> {respuesta.paciente?.nombres} {respuesta.paciente?.apellidos}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="body2">
                <strong>Especialista:</strong> {respuesta.especialista?.usuario?.nombre} {respuesta.especialista?.usuario?.apellidos}
              </Typography>
              
              {respuesta.especialista?.num_colegiatura && (
                <Typography variant="body2">
                  <strong>Nº Colegiatura:</strong> {respuesta.especialista.num_colegiatura}
                </Typography>
              )}
              
              <Typography variant="body2">
                <strong>Historia Clínica:</strong> {respuesta.paciente?.numero_historia}
              </Typography>
            </Grid>
          </Grid>
          
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PdfIcon />}
              onClick={handleRegenerarPDF}
              disabled={generandoPdf}
            >
              Regenerar PDF
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default FormularioPdfViewer;